/**
 * Job priority presentation values.
 *
 * These are the *visual treatments* the UI renders, not the full set of
 * priority rows that exist in the backend (which is data-driven and may grow).
 * Backend priority names (Critical, High, Medium, Low, Unknown, ...) are mapped
 * onto these via `mapPriority` in the schedule page before reaching components.
 *
 * `unknown` is a real, selectable backend priority (`job_priority` id 5), not just
 * an absent value: the priority migration assigned it to every job whose free-text
 * priority was NULL/blank/unrecognised, and inbound CORE work orders fall back to
 * it. It must therefore render as its own treatment — previously these jobs were
 * silently labelled "Low", so filtering by Unknown looked like it returned Low
 * jobs (SC-6368).
 *
 * Ordered most → least severe, with `unknown` last as it conveys no severity.
 */
export const JOB_PRIORITIES = ['critical', 'high', 'medium', 'low', 'unknown'] as const;

export type JobPriority = (typeof JOB_PRIORITIES)[number];

/**
 * Map a backend `job_priority.name` onto the UI's priority treatment.
 *
 * Every known name is matched explicitly and anything else — including `Unknown`,
 * null and a blank string — resolves to `'unknown'`. A previous `'low'` fallback
 * mislabelled the whole Unknown bucket as "Low", which made the Schedule priority
 * filter look broken: selecting Unknown returned the correct jobs but every card
 * read "Low Priority" (SC-6368).
 */
export function mapPriority(name: string | null | undefined): JobPriority {
  switch ((name ?? '').trim().toLowerCase()) {
    case 'critical':
      return 'critical';
    case 'high':
      return 'high';
    case 'medium':
      return 'medium';
    case 'low':
      return 'low';
    default:
      return 'unknown';
  }
}
